import {useTheme} from '@mui/material';
import {Portal} from 'presentation/components';
import React, {useEffect, useState} from 'react';
import Joyride, {ACTIONS, CallBackProps, STATUS} from 'react-joyride';
import {translations} from 'presentation/translations-new';
import {Tooltip} from './Tooltip';
import {TutorialProps} from './types';

const {close, goBack, skip, goForward, okUnderstood} = translations['pt-br'].commons;

export default function Tutorial({
	steps,
	storageKey,
	run,
	persistOnMount = true,
	onComplete,
	onClose,
	onSecondaryAction,
	customButtonsLabels,
	customContainerWidth,
	disableScrollParentFix = true,
	disableScrolling = true,
	disableOverlayClose = false,
	disableAutoScroll = false,
	scrollOffset,
	onStepChange,
}: TutorialProps) {
	const [showTourState, setShowTourState] = useState(false);
	const isControlled = typeof run === 'boolean';

	const theme = useTheme();

	const onFirstAccess = (storedValue: string) => {
		const storedTutorialValue = localStorage.getItem(storedValue);

		if (!storedTutorialValue) {
			localStorage.setItem(storedValue, 'true');
			setShowTourState(true);
			return;
		}

		setShowTourState(false);
	};

	useEffect(() => {
		if (isControlled || !persistOnMount) {
			return;
		}

		onFirstAccess(storageKey);
	}, [isControlled, persistOnMount, storageKey]);

	const handleJoyrideCallback = (data: CallBackProps) => {
		const {action, status, type, step} = data;

		onStepChange?.(data);

		if (type === 'step:before') {
			if (disableAutoScroll) return;
			const target = document.querySelector(step.target as string);
			if (!target) return;

			const rect = target.getBoundingClientRect();

			const isVisible =
				rect.top >= 0 &&
				rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);

			if (!isVisible) {
				target.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
				});
			}
		}

		if (status === STATUS.FINISHED) {
			setShowTourState(false);
			onComplete?.();
			return;
		}

		if (status === STATUS.SKIPPED) {
			setShowTourState(false);
			if (onSecondaryAction) {
				onSecondaryAction();
				return;
			}

			onComplete?.();
			return;
		}

		if (action === ACTIONS.CLOSE) {
			setShowTourState(false);
			if (onClose) {
				onClose();
				return;
			}

			onComplete?.();
		}
	};

	return (
		<Portal>
			<Joyride
				run={isControlled ? run : showTourState}
				steps={steps}
				callback={handleJoyrideCallback}
				hideCloseButton
				hideBackButton
				disableScrollParentFix={disableScrollParentFix}
				disableScrolling={disableScrolling}
				disableOverlayClose={disableOverlayClose}
				scrollOffset={scrollOffset}
				continuous={true}
				locale={{
					back: customButtonsLabels?.goBack ?? goBack,
					close: customButtonsLabels?.close ?? close,
					last: customButtonsLabels?.last ?? okUnderstood,
					next: customButtonsLabels?.next ?? goForward,
					skip: customButtonsLabels?.skip ?? skip,
				}}
				styles={{
					options: {
						zIndex: 10000,
						backgroundColor: theme.palette.background.paper,
					},
				}}
				tooltipComponent={props => (
					<Tooltip
						{...props}
						customContainerWidth={customContainerWidth}
						secondaryLabel={customButtonsLabels?.secondary ?? customButtonsLabels?.skip}
					/>
				)}
			/>
		</Portal>
	);
}
